import { ChatStatusEnum, newMessageReceived } from '@/entities/feedbacks'
import { type ChatListModel, getApiFeedbacks } from '@/shared/api'
import { createQueryModel } from '@/shared/lib/model-factories'
import { combine, createEvent, createStore, sample } from 'effector'
import { createGate } from 'effector-react'
import { and, throttle } from 'patronum'

import {
  $feedbackChatStatus,
  $feedbackChatUnreadMessagesCount,
  $loadingAllUnreadAfterChangingChat,
  FeedbackChatGate,
  feedbackChatQuery,
} from './feedback-chat.model'

export const FeedbacksPageGate = createGate()

const feedbacksQueryModel = createQueryModel({
  handler: getApiFeedbacks,
  initialFilters: {
    Statuses: [ChatStatusEnum.Opened, ChatStatusEnum.Closed],
  },
  pagination: true,
  accumulate: (prevStore: ChatListModel[], response) => [
    ...prevStore,
    ...response.items,
  ],
})

export const {
  availableSet,
  unavailableSet,
  filtersInit: feedbacksFiltersInit,
  $filters: $feedbacksFilters,
  filtersChanged: feedbacksFiltersChanged,
  filtersReset: feedbacksFiltersReset,
  pageChanged: feedbacksPageChanged,
  $page: $feedbacksPage,
  pageReset: feedbacksPageReset,
  query: feedbacksQuery,
  $search: $feedbacksSearch,
  searchChanged: feedbacksSearchChanged,
  $accumulatedData: $feedbacksData,
} = feedbacksQueryModel

// Search input

export const $feedbacksSearchInputValue = createStore('').on(
  feedbacksSearchChanged,
  (_, q) => q,
)

sample({
  clock: FeedbacksPageGate.open,
  target: availableSet,
})

sample({
  clock: availableSet,
  fn: () => ({}),
  target: feedbacksFiltersInit,
})

sample({
  clock: FeedbacksPageGate.close,
  target: unavailableSet,
})

sample({
  clock: $feedbackChatStatus,
  source: combine({
    feedbacksData: $feedbacksData,
    chatGate: FeedbackChatGate.state,
  }),
  fn: (
    { feedbacksData, chatGate: { chatId: currentChatId } },
    feedbackChatStatus,
  ) => {
    if (!currentChatId) return feedbacksData

    return feedbacksData.map((item) => {
      if (item.id === currentChatId && feedbackChatStatus !== null) {
        return {
          ...item,
          status: feedbackChatStatus,
        }
      }
      return item
    })
  },
  target: $feedbacksData,
})

sample({
  clock: $feedbackChatUnreadMessagesCount,
  source: combine({
    feedbacksData: $feedbacksData,
    chatGate: FeedbackChatGate.state,
  }),
  filter: $loadingAllUnreadAfterChangingChat.map((loading) => !loading),
  fn: (
    { feedbacksData, chatGate: { chatId: currentChatId } },
    feedbackChatUnreadMessagesCount,
  ) => {
    if (!currentChatId) return feedbacksData
    return feedbacksData.map((item) => {
      if (item.id === currentChatId) {
        return {
          ...item,
          newMessageCount: feedbackChatUnreadMessagesCount,
        }
      }
      return item
    })
  },
  target: $feedbacksData,
})

/**
 * Feature: Догрузка следующих страниц чатов
 */
export const feedbacksReachedEnd = createEvent()
const feedbacksNextPage = createEvent()

throttle({
  source: feedbacksReachedEnd,
  timeout: 300,
  target: feedbacksNextPage,
})

export const $feedbacksHasNextPage = feedbacksQuery.$data.map((data) => {
  if (!data) return false
  const { totalItems, itemsOffset, itemsQuantity } = data
  return totalItems > itemsOffset + itemsQuantity
})

sample({
  clock: feedbacksNextPage,
  source: $feedbacksPage,
  filter: and(
    $feedbacksHasNextPage,
    feedbacksQuery.$pending.map((pending) => !pending),
  ),
  fn: (page) => page + 1,
  target: feedbacksPageChanged,
})

/**
 * Feature: При получении новых сообщений обновляем количество непрочитанных
 * сообщений в списке чатов
 */

sample({
  clock: newMessageReceived,
  source: { feedbacksData: $feedbacksData, gate: FeedbackChatGate.state },
  fn: (
    { feedbacksData, gate: { chatId: currentChatId } },
    newMessageRecievedChat,
  ) =>
    feedbacksData.map((chat) => {
      if (chat.id === newMessageRecievedChat.id && chat.id !== currentChatId) {
        const lastMessage = newMessageRecievedChat.messages.items[0]
        return {
          ...chat,
          type: newMessageRecievedChat.type,
          status: newMessageRecievedChat.status,
          lastMessage,
          newMessageCount: lastMessage.isCurrentUserMessage
            ? chat.newMessageCount
            : chat.newMessageCount + 1,
        }
      }
      return chat
    }),
  target: $feedbacksData,
})

sample({
  source: $feedbacksData,
  clock: feedbackChatQuery.finished.success,
  fn: (feedbacksData, { result: { id, name, chatPartnerAvatar } }) =>
    feedbacksData.map((chat) => {
      if (chat.id === id) {
        return {
          ...chat,
          name,
          chatPartnerAvatar,
        }
      }
      return chat
    }),
  target: $feedbacksData,
})
